import {
    ComputationStatus,
    RouteInstructionsAndMetrics,
    RouteMetrics,
    RouteRequestInput,
    RouteRequestInputType,
    RouteRequestResult,
    RouteRequestState,
    Trip,
    TripList
} from "../../generated/gpb/api/route";
import { Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import { setSelectedTime } from "../../features/surface/surfaceSlice";
import { setCurrentDay } from "../../features/time/TimelineSlice";
import { getDayStart } from "../risk/geoUtils";
import { getRouteBoundingBox } from "../risk/ThumbnailMapUtils";
import { setSelectionState, setTripState } from "../../features/riskViewer/riskViewerSlice";
import { openEdit, openMultistartSelection, openTripResults } from "../../features/Drawer/drawerSlice";
import { RouteId } from "../../generated/gpb/api/id";
import { dateMessageToDate } from "./form/TripFormTypeConversions";
import { setOpenRoadStopsAbsent, setOpenroadsVisibilityToFalse } from "../../features/mapDisplay/mapDisplaySlice";
import { RouteRequestResultAndLabel } from "../MultistartSelectionDrawer/MultistartSelectionDrawer";
import { hideAlerts } from "../../features/alertDisplay/alertDisplaySlice";


const ignoreWeather = "Shortest route";
const sureRouteInput = "SureRoute";
export const advisoryVariationTag = "Your Route";

export function statusText( status: ComputationStatus | undefined ): string
{
    switch ( status )
    {
        case undefined:
        case ComputationStatus.REQUEST_PROCESSING_STATUS_UNSET:
        case ComputationStatus.UNRECOGNIZED:
            return "UNKNOWN";
        case ComputationStatus.CANCELLED:
            return "CANCELLED";
        case ComputationStatus.CANCELLING:
            return "CANCELLING";
        case ComputationStatus.ERROR:
            return "ERROR";
        case ComputationStatus.DONE:
            return "FINISHED";
        case ComputationStatus.WAITING:
            return "SUBMITTED";
        case ComputationStatus.IN_PROGRESS:
            return "ROUTING";
    }
}
export function getProcessingRequestStatus( trip: Trip | undefined )
{
    return shouldUseMultistartComputationStatus( trip?.routeState )
        ? trip?.processingRequestStatusMultistart?.computationStatus
        : trip?.processingRequestStatus?.computationStatus;
}

export function shouldUseMultistartComputationStatus( state: RouteRequestState | undefined ): boolean
{
    switch ( state )
    {
        case RouteRequestState.MULTISTART_REQUEST:
        case RouteRequestState.MULTISTART_ERROR:
        case RouteRequestState.SHOW_OPTIONS:
            return true;
        case undefined:
        case RouteRequestState.ROUTE_SELECTED: //not sure if this is the right place for this
        case RouteRequestState.INITIAL_RUN_REQUEST:
        case RouteRequestState.INITIAL_RUN_ERROR:
        case RouteRequestState.MULTISTART_ELIGIBLE:
        case RouteRequestState.MULTISTART_INELIGIBLE:
            return false;
    }
    return false;
}

export function getErrors( request: Trip )
{
    const errors: Array<string> = [];
    const requestErrors = shouldUseMultistartComputationStatus( request.routeState )
        ? request?.processingRequestStatusMultistart?.errorMessages
        : request?.processingRequestStatus?.errorMessages;

    if ( requestErrors !== undefined )
    {
        requestErrors.forEach( ( each ) => errors.push( each ) );
    }
    return errors;
}
export function selectRouteToView(
    trip: Trip,
    dispatch: Dispatch<AnyAction>,
    isAdmin: boolean,
)
{
    setTimelineStartTime( trip, dispatch );
    const routeResult = getFilteredRouteResults( trip, isAdmin );
    const routeId = routeResult?.[0]?.element.routeId;

    if ( trip.Id?.hexString !== undefined )
    {
        updateTripViewState( dispatch, trip, routeResult, routeId );
    }
}

function setTimelineStartTime( trip: Trip | undefined, dispatch: Dispatch<AnyAction> )
{
    const startDate = trip?.routeRequestInput?.startDate;
    if ( startDate === undefined ) return;

    const t = new Date();
    t.setFullYear( startDate.year, startDate.month - 1, startDate.day ); // JS Date months are 0-indexed

    const startHourMin = trip?.routeRequestInput?.desiredStartTimeOfDay;
    if ( startHourMin !== undefined )
    {
        t.setHours( startHourMin.hour, startHourMin.minute );
    }

    dispatch( setSelectedTime( t.valueOf() ) );
    dispatch( setCurrentDay( getDayStart( t.valueOf() ) ) );
}


function getFilteredRouteResults( trip: Trip, isAdmin: boolean ): Array<RouteRequestResultAndLabel> | undefined
{

    const selectedRoute = ( trip.routeState === RouteRequestState.MULTISTART_ELIGIBLE || trip.routeState === RouteRequestState.MULTISTART_INELIGIBLE )
        ? trip.routeResult.find( result => result.variationTag ===
            ( trip.routeRequestInput?.requestType === RouteRequestInputType.ADVISORY ? advisoryVariationTag : sureRouteInput ) )?.routeId
        : ( trip.selectedRouteHex === "" || trip.selectedRouteHex === undefined ) ? undefined : { hexString: trip.selectedRouteHex };

    return filterRawRouteResults(
        trip?.routeResult,
        selectedRoute,
        isAdmin,
    );
}

function updateTripViewState(
    dispatch: Dispatch<AnyAction>,
    trip: Trip,
    routeResult: Array<RouteRequestResultAndLabel> | undefined,
    routeId: RouteId | undefined
)
{
    const waypoints = trip.routeRequestInput?.waypoints;
    const newViewState = getRouteBoundingBox( waypoints );

    dispatch( setTripState( {
        tripState: trip,
        routesToDisplay: routeResult?.map( each => each.element ),
        selectedRoute: routeId,
        homeViewState: newViewState,
    } ) );
    // This disables open roads visibility when the user immediately opens a trip to prevent some glaring pop-in.
    // once the performance issues are addressed, we should remove it.
    dispatch( setOpenroadsVisibilityToFalse() );
    dispatch( hideAlerts() );
    dispatch( setOpenRoadStopsAbsent() );

    //the latest trip is always stored in the 0 index of the list. When we update the database to remove the revision level of nesting this can be removed.
    const routeState = trip.routeState;
    const originalTrip = trip;

    if ( routeState === RouteRequestState.ROUTE_SELECTED && trip.selectedRouteHex !== undefined )
    {
        dispatch( openTripResults( { trip: originalTrip, routeId: { hexString: trip.selectedRouteHex } } ) );
    }
    else if ( shouldUseMultistartComputationStatus( routeState ) )
    {
        dispatch( openMultistartSelection( { trip: originalTrip, routeId } ) );
    }
    else
    {
        dispatch( openTripResults( { trip: originalTrip, routeId } ) );
    }
}

export function filterRawRouteResults(
    routeResult: Array<RouteRequestResult>,
    selectedRoute: RouteId | undefined,
    isAdmin: boolean,
): Array<RouteRequestResultAndLabel>
{
    if ( selectedRoute !== undefined )
    {
        return [ { element: routeResult?.filter( result => result.routeId?.hexString === selectedRoute.hexString )[0], label: "selected" } ];
    }
    if ( isAdmin )
    {
        return sortByFinalScore( undefined, routeResult, "asc" );
    }

    const sureRouteRoute = getSureRouteRoute( routeResult );

    const sortedByFinalScore = [ ...routeResult ].sort( ( a, b ) =>
    {
        const scoreA = a.routeRankingVector?.finalScore ?? 0;
        const scoreB = b.routeRankingVector?.finalScore ?? 0;

        const epsilon = 0.01;

        if ( Math.abs( scoreA - scoreB ) > epsilon )
        {
            return scoreA - scoreB;
        }

        // Tiebreaker: prioritize sureRouteRoute
        if ( a.routeId === sureRouteRoute?.routeId ) return -1;
        if ( b.routeId === sureRouteRoute?.routeId ) return 1;
        return 0;
    } );

    const topCostRoutes = sortedByFinalScore
        .filter( route => route.variationTag !== ignoreWeather )
        .slice( 0, 2 );

    const uniqueRoutes: Array<RouteRequestResult> = [];

    [ ...topCostRoutes, sureRouteRoute ].forEach( route =>
    {
        if ( route !== undefined && !uniqueRoutes.some( r => r.routeId?.hexString === route.routeId?.hexString ) )
        {
            uniqueRoutes.push( route );
        }
    } );

    if ( uniqueRoutes.length < 3 )
    {
        for ( const candidate of sortedByFinalScore )
        {
            const alreadyIncluded = uniqueRoutes.some( r => r.routeId?.hexString === candidate.routeId?.hexString );
            if ( !alreadyIncluded && candidate.variationTag !== ignoreWeather )
            {
                uniqueRoutes.push( candidate );
                if ( uniqueRoutes.length === 3 ) break;
            }
        }
    }

    const sortedUniqueRoutes = sortByFinalScore( undefined, uniqueRoutes, "asc" );

    return sortedUniqueRoutes;
}

export function sortByFinalScore(
    metricsAndInstructions: RouteInstructionsAndMetrics | undefined,
    routes: Array<RouteRequestResult>,
    order: "asc" | "desc" = "desc"
): Array<RouteRequestResultAndLabel>
{
    const score = ( r: RouteRequestResult ) => r.routeRankingVector?.finalScore;

    const sorted = routes.slice().sort( ( a, b ) =>
    {
        const scoreA = score( a ) ?? ( order === "asc" ? Infinity : -Infinity );
        const scoreB = score( b ) ?? ( order === "asc" ? Infinity : -Infinity );
        return order === "asc" ? scoreA - scoreB : scoreB - scoreA;
    } );

    return addLabelsToSortedRoutes( sorted, metricsAndInstructions );

}
function addLabelsToSortedRoutes( sortedRouteResults: Array<RouteRequestResult>,
    metrics: RouteInstructionsAndMetrics | undefined ): Array<RouteRequestResultAndLabel>
{
    const labelsUsed: Array<undefined | string> = [ undefined, undefined, undefined ];
    // This check relies on the assumption that if the user got to multistart via advisory then their advisory route
    // encountered level 4 or 5, therefoer we never want to recommend that route to the user
    let recommendedIndex = 0;
    if ( sortedRouteResults[0] !== undefined && sortedRouteResults[0]?.variationTag === advisoryVariationTag && sortedRouteResults.length > 1 )
    {
        recommendedIndex = 1;
    }
    labelsUsed[recommendedIndex] = "Recommended";

    while ( labelsUsed.includes( undefined ) )
    {
        // find the first missing label
        const index = labelsUsed.indexOf( undefined );
        labelsUsed[index] = getLabel( sortedRouteResults[index], sortedRouteResults[recommendedIndex], metrics?.metrics[index], metrics?.metrics[recommendedIndex] );
    }
    const finalLabels: Array<string> = labelsUsed.map( each => each === undefined ? "" : each );
    return sortedRouteResults.map( ( each, eachIndex ) =>
    {
        return { element: each, label: finalLabels[eachIndex] };
    } );
}

function getLabel( current: RouteRequestResult, recommended: RouteRequestResult,
    currentMetrics: RouteMetrics | undefined, recommendedMetrics: RouteMetrics | undefined ): string
{
    const hasMetrics = currentMetrics !== undefined && recommendedMetrics !== undefined;
    if ( current === undefined ) return "";
    if ( current.variationTag === sureRouteInput ) return "Original route";
    if ( current.routeRankingVector === undefined || recommended.routeRankingVector === undefined )
    {
        return "Missing metrics";
    }
    if ( current.routeRankingVector.normalizedWeatherImpact < recommended.routeRankingVector.normalizedWeatherImpact )
    {
        return "Less weather";
    }
    if ( current.routeRankingVector.timeCost < recommended.routeRankingVector.timeCost )
    {
        return "Less driving time";
    }
    if ( hasMetrics && currentMetrics?.numberOfOvernights < recommendedMetrics?.numberOfOvernights )
    {
        return "Less overnights";
    }
    if ( !hasMetrics && current.routeRankingVector.overnightCost < recommended.routeRankingVector.overnightCost )
    {
        return "Less overnights";
    }
    if ( hasMetrics && currentMetrics?.totalWeatherDelayDurationMs < recommendedMetrics?.totalWeatherDelayDurationMs )
    {
        return "Less weather delays";
    }
    if ( current.routeRankingVector.idealWeatherHours > recommended.routeRankingVector.idealWeatherHours )
    {
        return "More time in good weather";
    }
    return "Alternative route";
}

function getSureRouteRoute(
    routes: Array<RouteRequestResult>
): RouteRequestResult | undefined
{
    return routes.find( route => route.variationTag === sureRouteInput );
}



export function formatDate( date: Date, includeTime = true )
{
    return (
        date.toLocaleDateString( "en-US", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year:
                date.getFullYear() !== new Date().getFullYear() ? "2-digit" : undefined,
        } ) +
        ( includeTime
            ? " - " +
            date.toLocaleTimeString( "en-US", {
                hour: "numeric",
                minute: "numeric",
            } )
            : "" )
    );
}
export function compareDates(
    d1: Date | undefined,
    d2: Date | undefined
): number
{
    if ( d1 === null || d2 === null || d1 === undefined || d2 === undefined ) return 0;
    return d1 < d2 ? -1 : d1 === d2 ? 0 : 1;
}
export function tripListToRows( trip: TripList | undefined ): Array<Trip>
{
    if ( trip === undefined ) return [];
    const list = TripList.fromJSON( trip );
    return list.trip
        .sort( ( a, b ) => -compareDates( a.lastModifiedDate, b.lastModifiedDate ) );
}

export function isPastStartDate( routeRequestInput: RouteRequestInput ): boolean
{
    if (
        routeRequestInput.startDate === undefined ||
        routeRequestInput.waypoints === undefined ||
        routeRequestInput.waypoints.length === 0
    )
    {
        return false;
    }
    const waypointStartTime = routeRequestInput.waypoints[0].startTime;

    const timeSource = waypointStartTime !== undefined ? waypointStartTime : routeRequestInput.desiredStartTimeOfDay;
    const startDateTime = dateMessageToDate( routeRequestInput.startDate, timeSource );

    const now = new Date();

    return now > startDateTime;
}


export function editTrip(
    dispatch: Dispatch<AnyAction>,
    tripData: Trip | undefined,
    overrideRequestTypeSureRoute = false
)
{
    if ( tripData !== undefined && tripData.routeRequestInput !== undefined )
    {
        // Make a shallow copy of routeRequestInput
        const newRouteRequestInput = {
            ...tripData.routeRequestInput,
            ...( overrideRequestTypeSureRoute && {
                requestType: RouteRequestInputType.SUREROUTE
            } )
        };

        // Make a shallow copy of tripData with updated routeRequestInput
        const newTripData = {
            ...tripData,
            routeRequestInput: newRouteRequestInput
        };

        dispatch(
            openEdit( {
                trip: newTripData,
                routeRequestInput: newRouteRequestInput,
                isForecastUpdate: false
            } )
        );


        const waypoints = newRouteRequestInput?.waypoints;
        const homeViewState = getRouteBoundingBox( waypoints );
        if ( waypoints.length > 0 )
        {
            dispatch( setSelectionState( { waypoints, homeViewState } ) );
        }
    }
}
